import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import type { LocationCredential } from '@/app/session/startup-session';
import {
  type InventoryBuildingOption,
  type InventoryFloorplan,
  NewMeterLocationClient,
} from '@/features/inventorization/new-meter-location-client';
import {
  loadMeterLocationFloorplan,
  saveLocalMeterLocationFloorplan,
} from '@/features/inventorization/new-meter-location-floorplan';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import type { FloorplanBlobRecord } from '@/shared/db/schema';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

interface NewMeterLocationStepState {
  error: string | null;
  floorplan: InventoryFloorplan | null;
  floorplanRecord: FloorplanBlobRecord | null;
  floorplanImageUrl: string | null;
  isLoadingFloorplan: boolean;
}

export function useNewMeterLocationStep(input: {
  activeLocation: LocationCredential | null;
  buildingId: string | null;
  floor: string;
}) {
  const client = useMemo(() => new NewMeterLocationClient(), []);
  const { floorplanBlobs } = useInventoryAppServices();
  const { bootstrap } = useLocationBootstrap();
  const buildings = useMemo(
    () => deriveBuildingOptionsFromBootstrap(bootstrap?.buildings ?? []),
    [bootstrap?.buildings],
  );
  const [state, setState] = useState<NewMeterLocationStepState>({
    error: null,
    floorplan: null,
    floorplanRecord: null,
    floorplanImageUrl: null,
    isLoadingFloorplan: false,
  });

  const clearFloorplanImage = useCallback(() => {
    setState(current => {
      if (current.floorplanImageUrl) {
        URL.revokeObjectURL(current.floorplanImageUrl);
      }

      return { ...current, floorplanImageUrl: null };
    });
  }, []);

  const saveLocalFloorplan = useCallback(
    async (file: File) => {
      const activeLocation = input.activeLocation;
      const locationId = activeLocation?.locationId;

      if (!(activeLocation && locationId && input.floor)) {
        throw new Error(
          translateInventoryText(
            'app.inventory.position.floorplan.selectLocationAndFloor',
            'Select a location and floor before adding a floorplan.',
          ),
        );
      }

      clearFloorplanImage();

      const saved = await saveLocalMeterLocationFloorplan({
        buildingId: input.buildingId,
        file,
        floor: input.floor,
        floorplanBlobs,
        locationId,
      });

      setState(current => ({
        ...current,
        error: null,
        floorplan: saved.floorplan,
        floorplanImageUrl: URL.createObjectURL(saved.floorplanRecord.blob),
        floorplanRecord: saved.floorplanRecord,
        isLoadingFloorplan: false,
      }));
    },
    [clearFloorplanImage, floorplanBlobs, input.activeLocation, input.buildingId, input.floor],
  );

  useEffect(() => {
    const activeLocation = input.activeLocation;
    const locationId = activeLocation?.locationId;

    if (!(activeLocation && locationId && input.floor)) {
      clearFloorplanImage();
      setState(current => ({
        ...current,
        floorplan: null,
        floorplanRecord: null,
        isLoadingFloorplan: false,
      }));
      return;
    }

    let isCancelled = false;
    clearFloorplanImage();
    setState(current => ({
      ...current,
      error: null,
      floorplan: null,
      floorplanRecord: null,
      isLoadingFloorplan: true,
    }));

    void (async () => {
      const result = await loadMeterLocationFloorplan({
        buildingId: input.buildingId,
        client,
        floor: input.floor,
        floorplanBlobs,
        locationApiKey: activeLocation.apiKey,
        locationId,
      });

      return {
        floorplan: result.floorplan,
        floorplanRecord: result.floorplanRecord,
        floorplanImageUrl: result.imageBlob ? URL.createObjectURL(result.imageBlob) : null,
      };
    })()
      .then(result => {
        if (!isCancelled) {
          setState(current =>
            current.floorplanRecord?.pendingUpload
              ? { ...current, isLoadingFloorplan: false }
              : {
                  ...current,
                  error: null,
                  floorplan: result.floorplan,
                  floorplanImageUrl: result.floorplanImageUrl,
                  floorplanRecord: result.floorplanRecord,
                  isLoadingFloorplan: false,
                },
          );
        } else if (result.floorplanImageUrl) {
          URL.revokeObjectURL(result.floorplanImageUrl);
        }
      })
      .catch(error => {
        if (!isCancelled) {
          setState(current =>
            current.floorplanRecord?.pendingUpload
              ? { ...current, isLoadingFloorplan: false }
              : {
                  ...current,
                  error:
                    error instanceof Error
                      ? error.message
                      : translateInventoryText(
                          'app.inventory.position.floorplan.loadError',
                          'Unable to load floorplan.',
                        ),
                  floorplan: null,
                  floorplanImageUrl: null,
                  floorplanRecord: null,
                  isLoadingFloorplan: false,
                },
          );
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [clearFloorplanImage, client, floorplanBlobs, input.activeLocation, input.buildingId, input.floor]);

  useEffect(
    () => () => {
      if (state.floorplanImageUrl) {
        URL.revokeObjectURL(state.floorplanImageUrl);
      }
    },
    [state.floorplanImageUrl],
  );

  return { ...state, buildings, isLoadingBuildings: false, saveLocalFloorplan };
}

function deriveBuildingOptionsFromBootstrap(
  buildings: Array<{ code?: string | null; id: number | string; name: string }>,
): InventoryBuildingOption[] {
  return buildings
    .map(building => ({
      code: building.code,
      id: String(building.id),
      name: building.name,
    }))
    .toSorted((left, right) => left.name.localeCompare(right.name));
}
